import { useEffect, useMemo, useState } from "react";
import { Reorder, useDragControls } from "framer-motion";
import { Lock, Unlock, Save, Printer, FileDown, KeyRound, Search, Loader2, Eye, EyeOff, Plus, GripVertical, Check } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/Bits";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/store/AuthStore";
import { useLocalStorage } from "@/lib/useLocalStorage";
import { RatePrint } from "@/components/RatePrint";

const num = (v) => Number(v) || 0;
const rupee = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const todayISO = () => new Date().toISOString().slice(0, 10);

const CATS = [
  { id: "inch", label: "Hex Bolt · Inches" },
  { id: "metric", label: "Hex Bolt · Metric" },
  { id: "nut", label: "CF Nut Rate" },
];

const isMetricNut = (it) => it.cat === "nut" && /M/.test(it.name.toUpperCase().replace("NUT", ""));
const sizeLabel = (it) => {
  if (it.cat === "nut") return `${isMetricNut(it) ? "M" : ""}${it.dia} NUT`;
  if (it.cat === "metric") return `M${it.dia} ${it.thread} ${it.len}`.replace(/\s+/g, " ").trim();
  return `${it.dia} ${it.thread} ${it.len}`.replace(/\s+/g, " ").trim();
};

function ReorderRow({ it, extraVal, onExtra, enabled, onToggle, base, show88, in88, onToggle88 }) {
  const controls = useDragControls();
  const rate = base + num(extraVal);
  return (
    <Reorder.Item
      value={it}
      dragListener={false}
      dragControls={controls}
      className={`flex flex-wrap items-center justify-between gap-2 px-3 py-1.5 ${enabled ? "bg-slate-900/40" : "bg-red-950/30 opacity-60"}`}
      data-testid={`rate-row-${it.name}`}
    >
      <div className="flex items-center gap-2">
        <button
          onPointerDown={(e) => controls.start(e)}
          className="cursor-grab touch-none text-slate-500 hover:text-slate-300"
          data-testid={`rate-drag-${it.name}`}
          title="Drag to reorder"
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <button
          data-testid={`rate-toggle-${it.name}`}
          onClick={onToggle}
          className={enabled ? "text-emerald-400 hover:text-emerald-300" : "text-red-400 hover:text-red-300"}
          title={enabled ? "Disable (hide from list)" : "Enable"}
        >
          {enabled ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
        </button>
        <span className={`font-mono text-sm font-semibold ${enabled ? "text-slate-200" : "text-slate-500 line-through"}`}>{sizeLabel(it)}</span>
      </div>
      <div className="flex items-center gap-2">
        {show88 && (
          <button
            data-testid={`rate-in88-${it.name}`}
            onClick={onToggle88}
            className={`flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-bold transition-colors ${in88 ? "bg-sky-500/20 text-sky-300" : "bg-slate-800 text-slate-500 hover:bg-slate-700"}`}
            title="Include this size in the 8.8 rate list"
          >
            <span className={`flex h-3.5 w-3.5 items-center justify-center rounded-[3px] border ${in88 ? "border-sky-400 bg-sky-500 text-slate-950" : "border-slate-600"}`}>
              {in88 && <Check className="h-3 w-3" />}
            </span>
            8.8
          </button>
        )}
        <Input
          inputMode="decimal"
          data-testid={`rate-extra-${it.name}`}
          value={extraVal}
          onChange={(e) => onExtra(e.target.value)}
          className="h-8 w-20 rounded-md border-emerald-700/60 bg-slate-900/60 text-right font-mono text-xs font-bold text-emerald-200"
        />
        <span data-testid={`rate-value-${it.name}`} className="w-20 text-right font-mono text-sm font-black text-orange-400">
          {base ? rupee(rate) : "—"}
        </span>
      </div>
    </Reorder.Item>
  );
}

export function RatePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ updatedAt: null, updatedBy: null });
  const [basic, setBasic] = useLocalStorage("trbolt_rate_basic", "");
  const [nutBasic, setNutBasic] = useLocalStorage("trbolt_rate_nut_basic", "");
  const [grades, setGrades] = useLocalStorage("trbolt_rate_grades", { inch: "MS 4.6", metric: "MS 4.6", nut: "MS 4.6" });
  const [date, setDate] = useState(todayISO());
  const [query, setQuery] = useState("");

  // admin edit
  const [unlocked, setUnlocked] = useState(false);
  const [pin, setPin] = useState("");
  const [pinOpen, setPinOpen] = useState(false);
  const [edits, setEdits] = useState({}); // name -> extra (string)
  const [enabledEdits, setEnabledEdits] = useState({}); // name -> bool
  const [in88Edits, setIn88Edits] = useState({}); // name -> bool (include in 8.8 sheet)
  const [orderDirty, setOrderDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pinChangeOpen, setPinChangeOpen] = useState(false);
  const [curPin, setCurPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ kind: "inch", dia: "", thread: "FT", len: "", extra: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/rates");
      setItems(data.items || []);
      setMeta({ updatedAt: data.updatedAt, updatedBy: data.updatedBy });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const b = num(basic);
  const nb = num(nutBasic);
  const baseFor = (it) => (it.cat === "nut" ? nb : b);
  const isEnabled = (it) => (enabledEdits[it.name] !== undefined ? enabledEdits[it.name] : it.enabled !== false);
  const is88 = (it) => (in88Edits[it.name] !== undefined ? in88Edits[it.name] : it.in88 === true);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const out = {};
    for (const cat of CATS) out[cat.id] = {};
    for (const it of items) {
      const enabled = enabledEdits[it.name] !== undefined ? enabledEdits[it.name] : it.enabled !== false;
      if (!unlocked && !enabled) continue; // hide disabled items unless admin is editing
      if (q && !it.name.toLowerCase().includes(q) && !sizeLabel(it).toLowerCase().includes(q)) continue;
      const bucket = out[it.cat];
      if (!bucket) continue;
      const key = it.cat === "nut" ? "All Nuts" : it.cat === "metric" ? `M${it.dia} ${it.thread}` : `${it.dia}" ${it.thread}`;
      (bucket[key] = bucket[key] || []).push(it);
    }
    return out;
  }, [items, query, unlocked, edits, enabledEdits]);

  const printRows = useMemo(
    () =>
      items
        .filter((it) => (enabledEdits[it.name] !== undefined ? enabledEdits[it.name] : it.enabled !== false))
        .map((it) => ({
          ...it,
          label: sizeLabel(it),
          rate: (it.cat === "nut" ? nb : b) + num(it.extra),
        })),
    [items, b, nb, enabledEdits]
  );

  const doUnlock = async () => {
    try {
      await api.post("/rates/unlock", { pin });
      setUnlocked(true);
      setPinOpen(false);
      toast.success("Unlocked — you can now edit extra costs");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveEdits = async () => {
    const names = new Set([...Object.keys(edits), ...Object.keys(enabledEdits), ...Object.keys(in88Edits)]);
    const changed = [...names].map((name) => {
      const o = { name };
      if (edits[name] !== undefined) o.extra = num(edits[name]);
      if (enabledEdits[name] !== undefined) o.enabled = enabledEdits[name];
      if (in88Edits[name] !== undefined) o.in88 = in88Edits[name];
      return o;
    });
    if (!changed.length && !orderDirty) {
      toast.info("No changes to save");
      return;
    }
    setSaving(true);
    try {
      let latest = null;
      if (orderDirty) {
        const { data } = await api.put("/rates/order", { pin, order: items.map((i) => i.name) });
        latest = data;
      }
      if (changed.length) {
        const { data } = await api.put("/rates", { pin, items: changed });
        latest = data;
      }
      if (latest) {
        setItems(latest.items || []);
        setMeta({ updatedAt: latest.updatedAt, updatedBy: latest.updatedBy });
      }
      setEdits({});
      setEnabledEdits({});
      setIn88Edits({});
      setOrderDirty(false);
      toast.success("Changes saved");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const onReorder = (catId, newList) => {
    setItems((prev) => {
      let idx = 0;
      return prev.map((it) => (it.cat === catId ? newList[idx++] : it));
    });
    setOrderDirty(true);
  };

  const addItem = async () => {
    const f = addForm;
    const dia = f.dia.trim();
    if (!dia) {
      toast.error("Enter a diameter/size");
      return;
    }
    let cat, name, thread, len;
    if (f.kind === "inch") {
      cat = "inch"; thread = f.thread; len = f.len.trim(); name = `${dia}${thread}${len}`;
    } else if (f.kind === "metric") {
      cat = "metric"; thread = f.thread; len = f.len.trim(); name = `${dia}${thread}${len}`;
    } else if (f.kind === "inchnut") {
      cat = "nut"; thread = ""; len = ""; name = `${dia}NUT`;
    } else {
      cat = "nut"; thread = ""; len = ""; name = `${dia}MNUT`;
    }
    if ((f.kind === "inch" || f.kind === "metric") && !len) {
      toast.error("Enter a length");
      return;
    }
    try {
      const { data } = await api.post("/rates/item", { name, extra: num(f.extra), cat, dia, thread, len, pin });
      setItems(data.items || []);
      setMeta({ updatedAt: data.updatedAt, updatedBy: data.updatedBy });
      setAddOpen(false);
      setAddForm({ kind: f.kind, dia: "", thread: f.thread, len: "", extra: "" });
      toast.success(`Added ${sizeLabel({ cat, dia, thread, len, name })}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const changePin = async () => {
    try {
      await api.put("/rates/pin", { currentPin: curPin, newPin });
      setPinChangeOpen(false);
      setCurPin("");
      setNewPin("");
      if (curPin === pin) setPin(newPin);
      toast.success("Lock PIN changed");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const doPrint = () => {
    const dd = date ? date.split("-").reverse().join("-") : todayISO().split("-").reverse().join("-");
    const prev = document.title;
    document.title = `TR RATE LIST ${dd}`;
    const restore = () => {
      document.title = prev;
      window.removeEventListener("afterprint", restore);
    };
    window.addEventListener("afterprint", restore);
    window.print();
    setTimeout(restore, 1500);
  };

  const lock = () => {
    setUnlocked(false);
    setEdits({});
    setEnabledEdits({});
    setIn88Edits({});
    setOrderDirty(false);
    setPin("");
    toast.info("Locked");
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold text-white">Rate List</h1>
          <p className="text-sm text-slate-500">Enter bolt &amp; nut basic rates — every size auto-adds its extra cost</p>
        </div>
        {isAdmin &&
          (unlocked ? (
            <Button data-testid="rate-lock-btn" onClick={lock} variant="outline" className="rounded-xl border-emerald-700 text-emerald-300">
              <Unlock className="mr-1.5 h-4 w-4" /> Unlocked
            </Button>
          ) : (
            <Button data-testid="rate-unlock-btn" onClick={() => setPinOpen(true)} variant="outline" className="rounded-xl border-slate-700 text-slate-200">
              <Lock className="mr-1.5 h-4 w-4" /> Unlock to Edit
            </Button>
          ))}
      </div>

      {/* Basic rate + date */}
      <Card>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="block">
            <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-slate-400">Bolt Basic Rate</span>
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 font-mono text-sm font-bold text-slate-500">₹</span>
              <Input
                inputMode="decimal"
                data-testid="rate-basic-input"
                value={basic}
                onChange={(e) => setBasic(e.target.value)}
                placeholder="e.g. 3000"
                className="h-12 rounded-xl border-slate-700 bg-slate-900/60 pl-7 text-right font-mono text-lg font-bold text-white focus-visible:ring-orange-500/60"
              />
            </div>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-slate-400">Nut Basic Rate</span>
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 font-mono text-sm font-bold text-slate-500">₹</span>
              <Input
                inputMode="decimal"
                data-testid="rate-nut-basic-input"
                value={nutBasic}
                onChange={(e) => setNutBasic(e.target.value)}
                placeholder="e.g. 2800"
                className="h-12 rounded-xl border-slate-700 bg-slate-900/60 pl-7 text-right font-mono text-lg font-bold text-white focus-visible:ring-orange-500/60"
              />
            </div>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-slate-400">Date</span>
            <Input
              type="date"
              data-testid="rate-date-input"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="h-12 rounded-xl border-slate-700 bg-slate-900/60 font-mono text-base font-bold text-white focus-visible:ring-orange-500/60"
            />
          </label>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button data-testid="rate-print-btn" onClick={doPrint} disabled={!b && !nb} className="flex-1 rounded-xl bg-orange-500 font-bold text-slate-950 hover:bg-orange-400">
            <Printer className="mr-1.5 h-4 w-4" /> Print
          </Button>
          <Button data-testid="rate-pdf-btn" onClick={doPrint} disabled={!b && !nb} variant="outline" className="flex-1 rounded-xl border-slate-700 text-slate-200">
            <FileDown className="mr-1.5 h-4 w-4" /> Save PDF
          </Button>
        </div>
        {meta.updatedBy && (
          <p className="mt-2 text-[11px] text-slate-500">Extra costs last updated by {meta.updatedBy}</p>
        )}
      </Card>

      {/* Section grades */}
      <Card>
        <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-400">Grades (shown in headings)</div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="block">
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">Inch Bolt Grade</span>
            <Input data-testid="grade-inch" value={grades.inch} onChange={(e) => setGrades({ ...grades, inch: e.target.value })} placeholder="e.g. MS 4.6" className="h-10 rounded-lg border-slate-700 bg-slate-900/60 font-bold text-white" />
          </label>
          <label className="block">
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">Metric Bolt Grade</span>
            <Input data-testid="grade-metric" value={grades.metric} onChange={(e) => setGrades({ ...grades, metric: e.target.value })} placeholder="e.g. MS 8.8" className="h-10 rounded-lg border-slate-700 bg-slate-900/60 font-bold text-white" />
          </label>
          <label className="block">
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">Nut Grade</span>
            <Input data-testid="grade-nut" value={grades.nut} onChange={(e) => setGrades({ ...grades, nut: e.target.value })} placeholder="e.g. MS" className="h-10 rounded-lg border-slate-700 bg-slate-900/60 font-bold text-white" />
          </label>
        </div>
      </Card>

      {/* Admin edit toolbar */}
      {unlocked && (
        <Card className="border-emerald-800/60 bg-emerald-500/5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm font-semibold text-emerald-300">Editing · drag <GripVertical className="inline h-3.5 w-3.5" /> to reorder within a section</div>
            <div className="flex flex-wrap gap-2">
              <Button data-testid="rate-add-btn" onClick={() => setAddOpen(true)} variant="outline" size="sm" className="rounded-lg border-orange-600 text-orange-300">
                <Plus className="mr-1 h-3.5 w-3.5" /> Add Item
              </Button>
              <Button data-testid="rate-change-pin-btn" onClick={() => setPinChangeOpen(true)} variant="outline" size="sm" className="rounded-lg border-slate-700 text-slate-200">
                <KeyRound className="mr-1.5 h-3.5 w-3.5" /> Change PIN
              </Button>
              <Button data-testid="rate-save-btn" onClick={saveEdits} disabled={saving} size="sm" className="rounded-lg bg-emerald-500 font-bold text-slate-950 hover:bg-emerald-400">
                {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1.5 h-3.5 w-3.5" />} Save
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
        <Input
          data-testid="rate-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search size (e.g. 1/2, 16MM, 3/8FT)"
          className="h-11 rounded-xl border-slate-700 bg-slate-900/60 pl-9 text-white focus-visible:ring-orange-500/60"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
        </div>
      ) : unlocked ? (
        // ===== Edit / reorder mode =====
        CATS.map((cat) => {
          const catList = items.filter((it) => it.cat === cat.id);
          if (!catList.length) return null;
          return (
            <Card key={cat.id} className="p-0" data-testid={`rate-cat-${cat.id}`}>
              <div className="border-b border-slate-800 bg-slate-800/40 px-4 py-2.5 font-display text-sm font-extrabold uppercase tracking-wide text-orange-300">
                {cat.label}{grades[cat.id] ? ` — ${grades[cat.id]}` : ""}
              </div>
              <Reorder.Group axis="y" values={catList} onReorder={(nl) => onReorder(cat.id, nl)} className="divide-y divide-slate-800/60">
                {catList.map((it) => {
                  const enabled = isEnabled(it);
                  return (
                    <ReorderRow
                      key={it.name}
                      it={it}
                      base={baseFor(it)}
                      enabled={enabled}
                      extraVal={edits[it.name] !== undefined ? edits[it.name] : String(it.extra)}
                      onExtra={(v) => setEdits((p) => ({ ...p, [it.name]: v }))}
                      onToggle={() => setEnabledEdits((p) => ({ ...p, [it.name]: !enabled }))}
                      show88={it.cat === "metric"}
                      in88={is88(it)}
                      onToggle88={() => setIn88Edits((p) => ({ ...p, [it.name]: !is88(it) }))}
                    />
                  );
                })}
              </Reorder.Group>
            </Card>
          );
        })
      ) : (
        // ===== View mode =====
        CATS.map((cat) => {
          const bucket = groups[cat.id];
          const keys = Object.keys(bucket);
          if (!keys.length) return null;
          return (
            <Card key={cat.id} className="p-0" data-testid={`rate-cat-${cat.id}`}>
              <div className="border-b border-slate-800 bg-slate-800/40 px-4 py-2.5 font-display text-sm font-extrabold uppercase tracking-wide text-orange-300">
                {cat.label}{grades[cat.id] ? ` — ${grades[cat.id]}` : ""}
              </div>
              <div className="divide-y divide-slate-800/60">
                {keys.map((k) => (
                  <div key={k} className="px-4 py-2">
                    <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">{k}</div>
                    <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                      {bucket[k].map((it) => {
                        const extra = num(it.extra);
                        const base = baseFor(it);
                        const rate = base + extra;
                        return (
                          <div key={it.name} className="flex items-center justify-between gap-2 rounded-lg bg-slate-900/40 px-3 py-1.5" data-testid={`rate-row-${it.name}`}>
                            <span className="font-mono text-sm font-semibold text-slate-200">{sizeLabel(it)}</span>
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-[11px] text-slate-500">+{extra}</span>
                              <span data-testid={`rate-value-${it.name}`} className="w-20 text-right font-mono text-sm font-black text-orange-400">
                                {base ? rupee(rate) : "—"}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          );
        })
      )}

      {/* PIN unlock dialog */}
      {pinOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setPinOpen(false)}>
          <Card className="w-full max-w-xs" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center gap-2 font-display text-lg font-extrabold text-white">
              <Lock className="h-5 w-5 text-orange-400" /> Enter Lock PIN
            </div>
            <Input
              type="password"
              inputMode="numeric"
              data-testid="rate-pin-input"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doUnlock()}
              placeholder="PIN"
              className="h-12 rounded-xl border-slate-700 bg-slate-900/60 text-center font-mono text-lg tracking-widest text-white"
              autoFocus
            />
            <div className="mt-3 flex gap-2">
              <Button onClick={() => setPinOpen(false)} variant="outline" className="flex-1 rounded-xl border-slate-700 text-slate-300">Cancel</Button>
              <Button data-testid="rate-pin-submit" onClick={doUnlock} className="flex-1 rounded-xl bg-orange-500 font-bold text-slate-950 hover:bg-orange-400">Unlock</Button>
            </div>
          </Card>
        </div>
      )}

      {/* Change PIN dialog */}
      {pinChangeOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setPinChangeOpen(false)}>
          <Card className="w-full max-w-xs" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center gap-2 font-display text-lg font-extrabold text-white">
              <KeyRound className="h-5 w-5 text-orange-400" /> Change Lock PIN
            </div>
            <Input type="password" inputMode="numeric" data-testid="rate-curpin-input" value={curPin} onChange={(e) => setCurPin(e.target.value)} placeholder="Current PIN" className="mb-2 h-11 rounded-xl border-slate-700 bg-slate-900/60 text-center font-mono tracking-widest text-white" />
            <Input type="password" inputMode="numeric" data-testid="rate-newpin-input" value={newPin} onChange={(e) => setNewPin(e.target.value)} placeholder="New PIN (min 3)" className="h-11 rounded-xl border-slate-700 bg-slate-900/60 text-center font-mono tracking-widest text-white" />
            <div className="mt-3 flex gap-2">
              <Button onClick={() => setPinChangeOpen(false)} variant="outline" className="flex-1 rounded-xl border-slate-700 text-slate-300">Cancel</Button>
              <Button data-testid="rate-newpin-submit" onClick={changePin} className="flex-1 rounded-xl bg-orange-500 font-bold text-slate-950 hover:bg-orange-400">Save PIN</Button>
            </div>
          </Card>
        </div>
      )}

      {/* Add Item dialog */}
      {addOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setAddOpen(false)}>
          <Card className="w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center gap-2 font-display text-lg font-extrabold text-white">
              <Plus className="h-5 w-5 text-orange-400" /> Add New Item
            </div>
            <label className="mb-2 block">
              <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">Type</span>
              <Select value={addForm.kind} onValueChange={(v) => setAddForm({ ...addForm, kind: v })}>
                <SelectTrigger data-testid="add-kind" className="h-11 rounded-lg border-slate-700 bg-slate-900/60 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="border-slate-700 bg-slate-900 text-slate-100">
                  <SelectItem value="inch">Inch Bolt</SelectItem>
                  <SelectItem value="metric">Metric Bolt</SelectItem>
                  <SelectItem value="inchnut">Inch Nut</SelectItem>
                  <SelectItem value="metricnut">Metric Nut</SelectItem>
                </SelectContent>
              </Select>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">Diameter / Size</span>
                <Input data-testid="add-dia" value={addForm.dia} onChange={(e) => setAddForm({ ...addForm, dia: e.target.value })} placeholder={addForm.kind.includes("metric") ? "e.g. 12" : "e.g. 3/8"} className="h-11 rounded-lg border-slate-700 bg-slate-900/60 text-white" />
              </label>
              {(addForm.kind === "inch" || addForm.kind === "metric") && (
                <label className="block">
                  <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">Thread</span>
                  <Select value={addForm.thread} onValueChange={(v) => setAddForm({ ...addForm, thread: v })}>
                    <SelectTrigger data-testid="add-thread" className="h-11 rounded-lg border-slate-700 bg-slate-900/60 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="border-slate-700 bg-slate-900 text-slate-100">
                      <SelectItem value="FT">FT (Full Thread)</SelectItem>
                      <SelectItem value="HT">HT (Half Thread)</SelectItem>
                    </SelectContent>
                  </Select>
                </label>
              )}
              {(addForm.kind === "inch" || addForm.kind === "metric") && (
                <label className="block">
                  <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">Length</span>
                  <Input data-testid="add-len" value={addForm.len} onChange={(e) => setAddForm({ ...addForm, len: e.target.value })} placeholder={addForm.kind === "metric" ? "e.g. 40" : "e.g. 2.1/2"} className="h-11 rounded-lg border-slate-700 bg-slate-900/60 text-white" />
                </label>
              )}
              <label className="block">
                <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">Extra Cost</span>
                <Input inputMode="decimal" data-testid="add-extra" value={addForm.extra} onChange={(e) => setAddForm({ ...addForm, extra: e.target.value })} placeholder="e.g. 250" className="h-11 rounded-lg border-slate-700 bg-slate-900/60 text-right font-mono text-white" />
              </label>
            </div>
            <div className="mt-4 flex gap-2">
              <Button onClick={() => setAddOpen(false)} variant="outline" className="flex-1 rounded-xl border-slate-700 text-slate-300">Cancel</Button>
              <Button data-testid="add-submit" onClick={addItem} className="flex-1 rounded-xl bg-orange-500 font-bold text-slate-950 hover:bg-orange-400">Add</Button>
            </div>
          </Card>
        </div>
      )}

      {/* Print output */}
      <RatePrint rows={printRows} basic={b} nutBasic={nb} date={date} grades={grades} />
    </div>
  );
}
